import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Twitter, Instagram, Facebook, Youtube, Music, Link as LinkIcon, MapPin } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { DEFAULTS } from "@/lib/content_defaults";

export default function Contact() {
  const { data: hero } = useSiteContent("page_hero_contact", DEFAULTS.page_hero_contact);
  const { data: info } = useSiteContent("contact_info", DEFAULTS.contact_info);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [faq, setFaq] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/faq").then(r => setFaq(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Could not send message."); }
    setSending(false);
  };

  const socials = [
    { url: info.social_x, handle: info.social_x_handle || "Twitter / X", Icon: Twitter, testid: "contact-info-x" },
    { url: info.social_instagram, handle: info.social_instagram_handle || "Instagram", Icon: Instagram, testid: "contact-info-ig" },
    { url: info.social_facebook, handle: "Facebook", Icon: Facebook, testid: "contact-info-fb" },
    { url: info.social_youtube, handle: "YouTube", Icon: Youtube, testid: "contact-info-yt" },
    { url: info.social_tiktok, handle: "TikTok", Icon: Music, testid: "contact-info-tt" },
    { url: info.social_hudl, handle: "Hudl", Icon: LinkIcon, testid: "contact-info-hudl" },
  ].filter(s => s.url);

  return (
    <div>
      <section className="section-dark py-20">
        <div className="container-app">
          <div className="label-tag mb-4">{hero.label_tag}</div>
          <h1 className="font-heading font-black uppercase text-5xl md:text-7xl leading-none">
            {hero.line1}<br/><span className="metallic-gold">{hero.line2}</span>{hero.line3 && <><br/>{hero.line3}</>}
          </h1>
          {hero.subtitle && <p className="mt-6 text-white/70 max-w-2xl text-lg">{hero.subtitle}</p>}
        </div>
      </section>
      <section className="section-light py-16">
        <div className="container-app grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-6">
            {info.email && (
              <div>
                <div className="label-tag mb-2" style={{ color: "#000" }}>Email</div>
                <a href={`mailto:${info.email}`} data-testid="contact-info-email" className="flex items-center gap-2 font-heading uppercase text-lg break-all"><Mail size={18} className="flex-shrink-0"/>{info.email}</a>
              </div>
            )}
            {(info.phone_1 || info.phone_2) && (
              <div>
                <div className="label-tag mb-2" style={{ color: "#000" }}>Phone</div>
                <div className="space-y-1">
                  {info.phone_1 && <a href={`tel:${info.phone_1.replace(/\./g,'')}`} data-testid="contact-info-phone-1" className="flex items-center gap-2 font-heading uppercase text-lg"><Phone size={18}/>{info.phone_1}</a>}
                  {info.phone_2 && <a href={`tel:${info.phone_2.replace(/\./g,'')}`} data-testid="contact-info-phone-2" className="flex items-center gap-2 font-heading uppercase text-lg"><Phone size={18}/>{info.phone_2}</a>}
                </div>
              </div>
            )}
            {info.address && (
              <div>
                <div className="label-tag mb-2" style={{ color: "#000" }}>Address</div>
                <div className="flex items-start gap-2"><MapPin size={18}/>{info.address}</div>
              </div>
            )}
            {socials.length > 0 && (
              <div>
                <div className="label-tag mb-2" style={{ color: "#000" }}>Follow</div>
                <div className="space-y-1">
                  {socials.map(s => (
                    <a key={s.testid} href={s.url} target="_blank" rel="noreferrer" data-testid={s.testid}
                      className="flex items-center gap-2 font-heading uppercase text-lg hover:text-[color:var(--gold)] transition-colors">
                      <s.Icon size={18}/>{s.handle}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <form onSubmit={submit} className="lg:col-span-2 card-light p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label-tag" style={{ color: "#000" }}>Name</label>
                <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="input-sharp mt-2" data-testid="contact-name" />
              </div>
              <div>
                <label className="label-tag" style={{ color: "#000" }}>Email</label>
                <input type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="input-sharp mt-2" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="label-tag" style={{ color: "#000" }}>Subject</label>
              <input value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} className="input-sharp mt-2" data-testid="contact-subject" />
            </div>
            <div>
              <label className="label-tag" style={{ color: "#000" }}>Message</label>
              <textarea required rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-2 w-full border border-black/15 bg-white p-3 focus:border-[color:var(--gold)] outline-none" data-testid="contact-message"/>
            </div>
            <button disabled={sending} className="btn-gold" data-testid="contact-submit">{sending?"Sending…":"Send Message"}</button>
          </form>
        </div>
      </section>
      <section className="section-surface py-16">
        <div className="container-app">
          <div className="label-tag mb-4" style={{ color: "#000" }}>FAQ</div>
          <h2 className="font-heading font-black uppercase text-4xl mb-8">Frequently asked.</h2>
          <div className="max-w-3xl space-y-2" data-testid="faq-list">
            {faq.map((f,i) => (
              <div key={f.id || i} className="border-b border-black/15">
                <button onClick={()=>setOpen(open===i?null:i)} className="w-full flex justify-between items-center py-4 text-left font-heading uppercase font-bold" data-testid={`faq-q-${i}`}>
                  <span>{f.q}</span>
                  <span className="text-[color:var(--gold)]">{open===i?"−":"+"}</span>
                </button>
                {open===i && <div className="pb-4 text-neutral-700" data-testid={`faq-a-${i}`}>{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
